import { Component } from '@theme/component';
import { debounce, isClickedOutside, lockScroll, onAnimationEnd, unlockScroll } from '@theme/utilities';
import { getScrollTop, scrollTo } from '@theme/scroll-container';

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
    unlockScroll(this.refs.dialog);
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  #previousScrollY = 0;

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open) return;

    this.#previousScrollY = getScrollTop();

    // Prevent layout thrashing by separating DOM reads from DOM writes
    requestAnimationFrame(() => {
      lockScroll(dialog);

      dialog.showModal();
      this.dispatchEvent(new DialogOpenEvent());

      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    if (!dialog.open) return;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    // Force browser to restart animation by resetting it
    // Temporarily remove any existing animation state
    dialog.style.animation = 'none';

    // Force a reflow
    void dialog.offsetWidth;

    // Now add the closing class and restore animation
    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    unlockScroll(dialog);
    scrollTo({ top: this.#previousScrollY, behavior: 'instant' });

    dialog.close();
    dialog.classList.remove('dialog-closing');

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;
        if (open) {
          lockScroll(event.target);
        } else {
          unlockScroll(event.target);
        }
      }
    }
  },
  { capture: true }
);

/* Veloura password-page overlay fix (vel-pw-fix-11h)
 * Horizon's closed .password-dialog keeps 100vw×100svh + white dialog background,
 * covering the Coming Soon countdown. PasswordController HTML can stay sticky;
 * this module URL hash updates when the asset changes, so the fix still loads.
 */
(function velouraPasswordOverlayFix() {
  if (typeof document === 'undefined') return;
  if (!document.querySelector('.password-dialog, [data-vel-launch], #password-form')) return;

  const css = document.createElement('style');
  css.setAttribute('data-vel-pw-fix', '11h');
  css.textContent = `
    html, body { margin: 0 !important; background: #061912 !important; color: #FDF7ED !important; }
    dialog, dialog.password-dialog, dialog-component#password-form, #password-form, .password-dialog {
      display: none !important; opacity: 0 !important; visibility: hidden !important;
      pointer-events: none !important; width: 0 !important; height: 0 !important;
      max-width: 0 !important; max-height: 0 !important; position: fixed !important;
      z-index: -1 !important; background: transparent !important;
    }
    #MainContent.vel-launch, .vel-launch {
      position: relative !important; z-index: 10 !important; visibility: visible !important;
      opacity: 1 !important; display: grid !important; min-height: 100dvh !important;
      width: 100% !important; place-items: center !important;
      padding: clamp(24px, 4vw, 48px) !important; color: #FDF7ED !important;
      background: #061912 !important; overflow: hidden !important;
    }
    .vel-launch__bg {
      position: absolute !important; inset: 0 !important; z-index: 0 !important; pointer-events: none !important;
      background:
        radial-gradient(ellipse 80% 60% at 50% 0%, rgba(198,161,91,0.22), transparent 55%),
        linear-gradient(165deg, #0B3D2E 0%, #061912 55%, #0A2F24 100%) !important;
    }
    .vel-launch__inner { position: relative !important; z-index: 2 !important; width: min(640px, 100%) !important; text-align: center !important; }
    .vel-launch__eyebrow { margin: 0 0 12px !important; font: 600 11px/1.2 system-ui, sans-serif !important; letter-spacing: 0.28em !important; text-transform: uppercase !important; color: #C6A15B !important; }
    .vel-launch__title { margin: 0 0 14px !important; font: 400 clamp(2.4rem, 7vw, 3.75rem)/1.05 Georgia, serif !important; color: #FDF7ED !important; }
    .vel-launch__lede { margin: 0 auto 36px !important; max-width: 34em !important; font: 400 clamp(1rem, 2.2vw, 1.125rem)/1.55 system-ui, sans-serif !important; color: rgba(253,247,237,0.78) !important; }
    .vel-launch__lede strong { color: #C6A15B !important; }
    .vel-launch__timer { display: grid !important; grid-template-columns: repeat(4, minmax(0, 1fr)) !important; gap: clamp(8px, 2vw, 16px) !important; margin: 0 0 28px !important; }
    .vel-launch__unit { background: rgba(253,247,237,0.06) !important; border: 1px solid rgba(198,161,91,0.35) !important; padding: clamp(14px, 2.5vw, 22px) 8px !important; }
    .vel-launch__unit span { display: block !important; font: 650 clamp(1.6rem, 5vw, 2.4rem)/1 system-ui, sans-serif !important; color: #FDF7ED !important; }
    .vel-launch__unit small { display: block !important; margin-top: 8px !important; font: 500 10px/1 system-ui, sans-serif !important; letter-spacing: 0.18em !important; text-transform: uppercase !important; color: rgba(198,161,91,0.9) !important; }
    .vel-launch__note { margin: 0 !important; font: 400 13px/1.4 system-ui, sans-serif !important; color: rgba(253,247,237,0.55) !important; }
    .vel-launch-staff > button.vel-launch-staff__btn { display: none !important; }
    details.vel-pw-staff { position: fixed !important; bottom: 16px !important; right: 16px !important; z-index: 50 !important; }
    details.vel-pw-staff > summary { list-style: none !important; cursor: pointer !important; border: 1px solid rgba(198,161,91,0.5) !important; background: rgba(6,25,18,0.92) !important; color: #FDF7ED !important; font: 600 11px/1 system-ui, sans-serif !important; letter-spacing: 0.12em !important; text-transform: uppercase !important; padding: 10px 14px !important; }
    details.vel-pw-staff form { margin-top: 8px !important; padding: 12px !important; background: rgba(6,25,18,0.95) !important; border: 1px solid rgba(198,161,91,0.35) !important; display: grid !important; gap: 8px !important; }
    details.vel-pw-staff input, details.vel-pw-staff button { font: 500 14px/1.2 system-ui, sans-serif !important; padding: 10px 12px !important; border: 1px solid rgba(198,161,91,0.35) !important; }
    details.vel-pw-staff button { background: #C6A15B !important; color: #061912 !important; font-weight: 700 !important; cursor: pointer !important; }
  `;
  (document.head || document.documentElement).appendChild(css);

  const kill = () => {
    document.querySelectorAll('dialog.password-dialog, dialog-component#password-form, #password-form').forEach((el) => {
      try {
        if (el instanceof HTMLDialogElement && el.open) el.close();
      } catch (e) {}
      el.setAttribute('hidden', '');
      el.style.setProperty('display', 'none', 'important');
      el.style.setProperty('visibility', 'hidden', 'important');
      el.style.setProperty('pointer-events', 'none', 'important');
      el.style.setProperty('opacity', '0', 'important');
      el.style.setProperty('width', '0', 'important');
      el.style.setProperty('height', '0', 'important');
      el.style.setProperty('z-index', '-1', 'important');
      el.style.setProperty('background', 'transparent', 'important');
    });
  };
  kill();
  document.addEventListener('DOMContentLoaded', kill);
  window.addEventListener('load', kill);
  setTimeout(kill, 0);
  setTimeout(kill, 200);

  const ensureStaff = () => {
    if (document.querySelector('details.vel-pw-staff, details.vel-launch-staff, details.vel-launch-staff-gate')) return;
    if (!document.body) return;
    const d = document.createElement('details');
    d.className = 'vel-pw-staff';
    d.innerHTML =
      '<summary>Staff / password</summary>' +
      '<form method="post" action="/password" accept-charset="UTF-8">' +
      '<input type="hidden" name="form_type" value="storefront_password" />' +
      '<input type="hidden" name="utf8" value="✓" />' +
      '<input type="password" name="password" placeholder="Password" autocomplete="current-password" />' +
      '<button type="submit">Enter</button></form>';
    document.body.appendChild(d);
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', ensureStaff);
  } else {
    ensureStaff();
  }
  document.documentElement.setAttribute('data-vel-pw-fix', '11h');
})();
